<template>
  <component
    :is="standalone ? 'div' : 'v-dialog'"
    v-bind="hostBind"
    v-on="hostListeners"
  >
    <v-card class="plantillas" :class="{ 'plantillas--vista': standalone }">
      <v-toolbar color="#00A548" dark dense flat class="flex-grow-0">
        <v-toolbar-title class="text-subtitle-1 font-weight-medium">Plantillas {{ nombreEscuela }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn small depressed color="white" class="green--text text--darken-3 mr-1" @click="abrirEditor()">
          <v-icon left small>mdi-plus</v-icon>Nueva
        </v-btn>
        <v-btn v-if="!standalone" icon aria-label="Cerrar" @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
      </v-toolbar>

      <div class="plantillas__filtros">
        <v-text-field
          v-model="buscar"
          placeholder="Buscar por nombre o texto"
          prepend-inner-icon="mdi-magnify"
          hide-details
          dense
          filled
          clearable
          class="plantillas__buscar"
        ></v-text-field>
        <v-checkbox v-model="verInactivas" label="Ver inactivas" hide-details dense class="mt-0 pt-0"></v-checkbox>
      </div>

      <v-card-text class="plantillas__cuerpo">
        <div v-if="cargando" class="plantillas__grid">
          <v-skeleton-loader v-for="n in 6" :key="n" type="image, list-item-two-line"></v-skeleton-loader>
        </div>

        <div v-else-if="!plantillas.length" class="plantillas__vacio">
          <v-icon size="40" color="green darken-2">mdi-message-image-outline</v-icon>
          <p class="text-subtitle-1 font-weight-medium mb-1">Aún no hay plantillas de {{ nombreEscuela }}</p>
          <p class="text-body-2 mb-4">Guarda aquí los mensajes que se mandan seguido: el texto, la imagen, los botones y cada cuánto se usan.</p>
          <v-btn color="#00A548" dark depressed @click="abrirEditor()"><v-icon left>mdi-plus</v-icon>Crear la primera</v-btn>
        </div>

        <div v-else-if="!filtradas.length" class="plantillas__vacio">
          <p class="text-body-2 mb-3">Ninguna plantilla coincide con los filtros.</p>
          <v-btn small text color="green darken-2" @click="limpiarFiltros">Quitar filtros</v-btn>
        </div>

        <div v-else class="plantillas__grid">
          <article
            v-for="item in filtradas"
            :key="item.idplantillas"
            class="plantilla"
            :class="{ 'plantilla--inactiva': !item.activo }"
          >
            <div class="plantilla__chat">
              <div class="burbuja">
                <img v-if="item.imagen" :src="urlImagen(item.imagen)" :alt="'Imagen de ' + item.titulo" class="burbuja__img" loading="lazy" />
                <div class="burbuja__texto burbuja__texto--corto" v-html="formatear(item.body)"></div>
                <div v-if="listaBotones(item).length" class="burbuja__botones">
                  <span v-for="(b, i) in listaBotones(item)" :key="i" class="burbuja__boton">{{ b.texto }}</span>
                </div>
              </div>
            </div>

            <div class="plantilla__info">
              <div class="plantilla__nombre" :title="item.titulo">{{ item.titulo }}</div>
              <div class="plantilla__etiquetas">
                <span class="etiqueta"><v-icon x-small class="mr-1">mdi-calendar-sync</v-icon>{{ textoRecurrencia(item.recurrencia) }}</span>
                <span v-if="listaBotones(item).length" class="etiqueta"><v-icon x-small class="mr-1">mdi-gesture-tap-button</v-icon>{{ listaBotones(item).length }} botón{{ listaBotones(item).length === 1 ? '' : 'es' }}</span>
                <span v-if="!item.activo" class="etiqueta etiqueta--inactiva">Inactiva</span>
              </div>
            </div>

            <div class="plantilla__acciones">
              <v-btn small outlined color="green darken-3" @click="copiar(item)">
                <v-icon left small>mdi-content-copy</v-icon>Copiar
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn icon small aria-label="Editar" @click="abrirEditor(item)"><v-icon small>mdi-pencil</v-icon></v-btn>
              <v-menu left offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small aria-label="Más opciones" v-bind="attrs" v-on="on"><v-icon small>mdi-dots-vertical</v-icon></v-btn>
                </template>
                <v-list dense>
                  <v-list-item @click="cambiarActivo(item)">
                    <v-list-item-title>{{ item.activo ? 'Desactivar' : 'Activar' }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="porEliminar = item">
                    <v-list-item-title class="error--text">Eliminar</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </article>
        </div>
      </v-card-text>
    </v-card>

    <!-- Editor -->
    <v-dialog v-model="editor" max-width="920" :fullscreen="$vuetify.breakpoint.xsOnly" persistent scrollable>
      <v-card v-if="form">
        <v-toolbar flat dense class="flex-grow-0 editor__barra">
          <v-toolbar-title class="text-subtitle-1 font-weight-medium">{{ form.idplantillas ? 'Editar plantilla' : 'Nueva plantilla' }} · {{ nombreEscuela }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon aria-label="Cerrar" :disabled="guardando" @click="editor = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>

        <v-card-text class="editor">
          <v-form ref="form" class="editor__form" @submit.prevent="guardar">
            <v-text-field
              v-model="form.titulo"
              label="Nombre de la plantilla"
              :rules="[r.nombre]"
              counter="100"
              filled
              dense
            ></v-text-field>

            <v-textarea
              v-model="form.body"
              label="Texto"
              :rules="[r.texto]"
              :counter="MAX_TEXTO"
              :hint="hintTexto"
              persistent-hint
              auto-grow
              rows="5"
              filled
              dense
              class="mb-1"
            ></v-textarea>
            <v-btn small text color="green darken-2" class="mb-3 px-0" @click="insertarFecha">
              <v-icon left small>mdi-calendar</v-icon>{{ textoInsertarFecha }}
            </v-btn>

            <div class="editor__label">Imagen</div>
            <div class="editor__imagen">
              <v-file-input
                v-model="archivo"
                accept="image/jpeg,image/png,image/webp"
                :label="form.imagen || vistaPrevia ? 'Cambiar imagen' : 'Subir imagen (opcional)'"
                prepend-icon=""
                prepend-inner-icon="mdi-image-plus"
                :rules="[r.imagen]"
                hint="JPG, PNG o WEBP de hasta 5 MB"
                persistent-hint
                filled
                dense
                show-size
              ></v-file-input>
              <v-btn v-if="form.imagen || vistaPrevia" small text color="error" class="mt-2" @click="quitarImagen">Quitar imagen</v-btn>
            </div>

            <div class="editor__label">Botones <span class="editor__opcional">(opcional, máx. {{ MAX_BOTONES }})</span></div>
            <div class="editor__botones">
              <div v-for="(b, i) in form.botones" :key="i" class="editor__boton">
                <v-select
                  v-model="b.tipo"
                  :items="tiposBoton"
                  label="Tipo"
                  filled
                  dense
                  hide-details
                  class="editor__boton-tipo"
                ></v-select>
                <v-text-field
                  v-model="b.texto"
                  label="Texto del botón"
                  :counter="MAX_TEXTO_BOTON"
                  :rules="[r.textoBoton]"
                  filled
                  dense
                  hide-details="auto"
                  class="editor__boton-texto"
                ></v-text-field>
                <v-text-field
                  v-if="b.tipo === 'url'"
                  v-model="b.url"
                  label="URL"
                  :rules="[r.urlBoton]"
                  filled
                  dense
                  hide-details="auto"
                  class="editor__boton-extra"
                ></v-text-field>
                <v-text-field
                  v-if="b.tipo === 'telefono'"
                  v-model="b.telefono"
                  label="Teléfono"
                  :rules="[r.telBoton]"
                  filled
                  dense
                  hide-details="auto"
                  class="editor__boton-extra"
                ></v-text-field>
                <v-btn icon small aria-label="Quitar botón" class="editor__boton-quitar" @click="quitarBoton(i)">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </div>
              <v-btn
                v-if="form.botones.length < MAX_BOTONES"
                small
                text
                color="green darken-2"
                class="px-0"
                @click="agregarBoton"
              >
                <v-icon left small>mdi-plus</v-icon>Agregar botón
              </v-btn>
              <p v-else class="editor__ayuda mb-0">Ya tienes el máximo de {{ MAX_BOTONES }} botones (límite de WhatsApp).</p>
            </div>

            <div class="editor__fila">
              <v-select
                v-model="form.recurrencia"
                :items="recurrencias"
                label="Recurrencia"
                hint="Cada cuánto se usa"
                persistent-hint
                filled
                dense
              ></v-select>
              <v-switch v-model="form.activo" :true-value="1" :false-value="0" label="Activa" color="green darken-2" inset hide-details class="mt-2"></v-switch>
            </div>
          </v-form>

          <aside class="editor__preview" aria-label="Vista previa">
            <div class="editor__label">Vista previa</div>
            <div class="plantilla__chat plantilla__chat--grande">
              <div class="burbuja">
                <img v-if="imagenEditor" :src="imagenEditor" alt="Imagen de la plantilla" class="burbuja__img" />
                <div v-if="form.body" class="burbuja__texto" v-html="formatear(form.body)"></div>
                <div v-else class="burbuja__texto burbuja__texto--vacio">El texto aparecerá aquí</div>
                <div v-if="form.botones && form.botones.length" class="burbuja__botones">
                  <span v-for="(b, i) in form.botones" :key="i" class="burbuja__boton">
                    <v-icon v-if="b.tipo === 'url'" x-small class="mr-1">mdi-open-in-new</v-icon>
                    <v-icon v-else-if="b.tipo === 'telefono'" x-small class="mr-1">mdi-phone</v-icon>
                    {{ b.texto || 'Botón' }}
                  </span>
                </div>
              </div>
            </div>
          </aside>
        </v-card-text>

        <v-card-actions class="px-4 pb-4">
          <v-spacer></v-spacer>
          <v-btn text :disabled="guardando" @click="editor = false">Cancelar</v-btn>
          <v-btn color="#00A548" dark depressed :loading="guardando" @click="guardar">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirmar eliminación -->
    <v-dialog :value="!!porEliminar" max-width="400" @input="porEliminar = null">
      <v-card v-if="porEliminar">
        <v-card-title class="text-subtitle-1 font-weight-medium">¿Eliminar «{{ porEliminar.titulo }}»?</v-card-title>
        <v-card-text>Deja de aparecer en el catálogo. Si solo quieres dejar de usarla por un tiempo, mejor desactívala.</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="guardando" @click="porEliminar = null">Cancelar</v-btn>
          <v-btn color="error" depressed :loading="guardando" @click="eliminar">Eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Elegir fecha al copiar plantilla con {{fecha}} -->
    <v-dialog v-model="dialogFecha" max-width="440" persistent>
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-medium">¿Qué fecha usamos?</v-card-title>
        <v-card-text>
          <p class="text-body-2 mb-3">Esta plantilla tiene <code v-pre>{{fecha}}</code>. Elige cómo rellenarla al copiar.</p>
          <v-radio-group v-model="fechaModo" hide-details class="mt-0">
            <v-radio value="ciclo" :disabled="!cicloFecha">
              <template v-slot:label>
                <span>
                  Inicio del ciclo actual
                  <span v-if="cicloFecha" class="grey--text text--darken-1">
                    ({{ cicloNombre }} · {{ formatearFechaHumana(cicloFecha) }})
                  </span>
                  <span v-else-if="cicloError" class="error--text"> — {{ cicloError }}</span>
                  <span v-else class="grey--text"> — cargando…</span>
                </span>
              </template>
            </v-radio>
            <v-radio value="manual" label="Elegir otra fecha"></v-radio>
          </v-radio-group>
          <v-menu
            v-if="fechaModo === 'manual'"
            v-model="menuFecha"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="formatearFechaHumana(fechaManual)"
                label="Fecha"
                prepend-inner-icon="mdi-calendar"
                readonly
                filled
                dense
                hide-details
                class="mt-3"
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="fechaManual"
              locale="es-mx"
              color="green darken-2"
              @input="menuFecha = false"
            ></v-date-picker>
          </v-menu>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="copiando" @click="cancelarFecha">Cancelar</v-btn>
          <v-btn color="#00A548" dark depressed :loading="copiando" :disabled="!fechaElegida" @click="confirmarCopiaConFecha">
            Copiar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="aviso.ver" timeout="2500" color="green darken-3" bottom>{{ aviso.texto }}</v-snackbar>
    <Alerta v-if="respuestaAlerta" :parametros="parametros" />
  </component>
</template>

<script>
  import Alerta         from '@/components/alertas/Alerta.vue'
  import validarErrores from '@/mixins/validarErrores'

  const MAX_TEXTO       = 1600
  const MAX_IMAGEN      = 5 * 1024 * 1024
  const MAX_BOTONES     = 3
  const MAX_TEXTO_BOTON = 20

  const RECURRENCIAS = [
    { value: 'unica',        text: 'Una sola vez' },
    { value: 'diaria',       text: 'Diaria' },
    { value: 'semanal',      text: 'Semanal' },
    { value: 'quincenal',    text: 'Quincenal' },
    { value: 'mensual',      text: 'Mensual' },
    { value: 'inicio_ciclo', text: 'Cada inicio de ciclo' },
  ]

  const TIPOS_BOTON = [
    { value: 'respuesta', text: 'Respuesta rápida' },
    { value: 'url',       text: 'Abrir enlace' },
    { value: 'telefono',  text: 'Llamar' },
  ]

  const escapar = (t) => String(t || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))

  const listaBotones = (item) => {
    const raw = item && item.botones
    if (!raw) return []
    if (Array.isArray(raw)) return raw
    try { return JSON.parse(raw) || [] } catch (_) { return [] }
  }

  const tieneFecha = (texto) => /\{\{\s*fecha\s*\}\}/i.test(String(texto || ''))

  const formatearFechaHumana = (ymd) => {
    if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ''
    const [y, m, d] = ymd.split('-').map(Number)
    const fecha = new Date(y, m - 1, d)
    return fecha.toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })
  }

  const aplicarFecha = (texto, ymd) => {
    const humana = formatearFechaHumana(ymd)
    return String(texto || '').replace(/\{\{\s*fecha\s*\}\}/gi, humana)
  }

  const hoyYmd = () => {
    const n = new Date()
    return [
      n.getFullYear(),
      String(n.getMonth() + 1).padStart(2, '0'),
      String(n.getDate()).padStart(2, '0'),
    ].join('-')
  }

  export default {
    name: 'PlantillasCatalogo',

    components: { Alerta },

    mixins: [ validarErrores ],

    props: {
      value:      { type: Boolean, default: false },
      escuela:    { type: Number, required: true },
      standalone: { type: Boolean, default: false },
    },

    data: () => ({
      plantillas: [],
      cargando: false,
      guardando: false,
      buscar: '',
      verInactivas: false,
      editor: false,
      form: null,
      archivo: null,
      vistaPrevia: null,
      porEliminar: null,
      aviso: { ver: false, texto: '' },
      recurrencias: RECURRENCIAS,
      tiposBoton: TIPOS_BOTON,
      MAX_TEXTO,
      MAX_BOTONES,
      MAX_TEXTO_BOTON,
      textoInsertarFecha: 'Insertar {{fecha}}',
      hintTexto: 'Usa *negritas*, _cursivas_ y ~tachado~. Para la fecha al copiar escribe {{fecha}}',
      dialogFecha: false,
      porCopiar: null,
      fechaModo: 'ciclo',
      fechaManual: hoyYmd(),
      menuFecha: false,
      cicloFecha: null,
      cicloNombre: '',
      cicloError: '',
      copiando: false,
    }),

    computed: {
      nombreEscuela () {
        return this.escuela == 2 ? 'FAST' : 'INBI'
      },

      hostBind () {
        if (this.standalone) return { class: 'plantillas-host plantillas-host--vista' }
        return {
          value: this.value,
          maxWidth: 1120,
          fullscreen: this.$vuetify.breakpoint.smAndDown,
          scrollable: true,
        }
      },

      hostListeners () {
        if (this.standalone) return {}
        return { input: (v) => this.$emit('input', v) }
      },

      fechaElegida () {
        if (this.fechaModo === 'ciclo') return this.cicloFecha
        return this.fechaManual
      },

      filtradas () {
        const q = String(this.buscar || '').trim().toLowerCase()
        return this.plantillas.filter((p) =>
          (this.verInactivas || p.activo) &&
          (!q || (p.titulo + ' ' + p.body).toLowerCase().includes(q))
        )
      },

      imagenEditor () {
        if (this.vistaPrevia) return this.vistaPrevia
        return this.form && this.form.imagen ? this.urlImagen(this.form.imagen) : null
      },

      r () {
        return {
          nombre: (v) => (String(v || '').trim().length >= 3 && String(v).trim().length <= 100) || 'Entre 3 y 100 caracteres',
          texto:  (v) => {
            const t = String(v || '').trim()
            if (!t) return 'Escribe el texto'
            return t.length <= MAX_TEXTO || 'Máximo ' + MAX_TEXTO + ' caracteres'
          },
          imagen: (f) => !f || f.size <= MAX_IMAGEN || 'La imagen no puede pesar más de 5 MB',
          textoBoton: (v) => {
            const t = String(v || '').trim()
            if (!t) return 'Escribe el texto del botón'
            return t.length <= MAX_TEXTO_BOTON || 'Máximo ' + MAX_TEXTO_BOTON + ' caracteres'
          },
          urlBoton: (v) => /^https?:\/\//i.test(String(v || '').trim()) || 'Debe empezar con http:// o https://',
          telBoton: (v) => {
            const t = String(v || '').replace(/[^\d+]/g, '')
            return (t.length >= 8 && t.length <= 20) || 'Teléfono no válido'
          },
        }
      },
    },

    watch: {
      value (abierto) {
        if (!this.standalone && abierto) this.cargarPlantillas()
      },

      archivo (f) {
        if (this.vistaPrevia) URL.revokeObjectURL(this.vistaPrevia)
        this.vistaPrevia = f ? URL.createObjectURL(f) : null
      },

      editor (abierto) {
        if (!abierto) this.archivo = null
      },
    },

    mounted () {
      if (this.standalone || this.value) this.cargarPlantillas()
    },

    beforeDestroy () {
      if (this.vistaPrevia) URL.revokeObjectURL(this.vistaPrevia)
    },

    methods: {
      cargarPlantillas () {
        this.cargando = true
        return this.$http.get('twilio.plantillas.catalogo', { params: { escuela: this.escuela } }).then((response) => {
          this.plantillas = response.data
        }).catch((error) => {
          this.validarError(error.response ? error.response.data.message : error.message)
        }).finally(() => { this.cargando = false })
      },

      urlImagen (nombre) {
        const api = String(this.$http.defaults.baseURL || '').replace(/\/?$/, '/')
        return api + 'imageneswhatsapplocal/' + nombre
      },

      textoRecurrencia (valor) {
        const r = RECURRENCIAS.find((x) => x.value === valor)
        return r ? r.text : 'Una sola vez'
      },

      listaBotones,

      agregarBoton () {
        if (!this.form || this.form.botones.length >= MAX_BOTONES) return
        this.form.botones.push({ tipo: 'respuesta', texto: '', url: '', telefono: '' })
      },

      quitarBoton (i) {
        this.form.botones.splice(i, 1)
      },

      formatearFechaHumana,

      insertarFecha () {
        if (!this.form) return
        const marca = '{{fecha}}'
        if (!String(this.form.body || '').includes(marca)) {
          this.form.body = (this.form.body ? this.form.body + ' ' : '') + marca
        }
      },

      // Formato de WhatsApp sobre texto ya escapado: *negrita*, _cursiva_, ~tachado~ y variables {{1}} / {{fecha}}.
      formatear (texto) {
        return escapar(texto)
          .replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>')
          .replace(/(^|[\s(])_([^_\n]+)_/g, '$1<em>$2</em>')
          .replace(/~([^~\n]+)~/g, '<s>$1</s>')
          .replace(/\{\{(\d+)\}\}/g, '<span class="burbuja__variable">{{$1}}</span>')
          .replace(/\{\{\s*fecha\s*\}\}/gi, '<span class="burbuja__variable">{{fecha}}</span>')
      },

      limpiarFiltros () {
        this.buscar = ''
        this.verInactivas = true
      },

      abrirEditor (item) {
        this.form = item
          ? {
            ...item,
            botones: listaBotones(item).map((b) => ({
              tipo: b.tipo || 'respuesta',
              texto: b.texto || '',
              url: b.url || '',
              telefono: b.telefono || '',
            })),
          }
          : { idplantillas: 0, titulo: '', body: '', imagen: null, botones: [], recurrencia: 'unica', activo: 1 }
        this.form.quitar_imagen = 0
        this.archivo = null
        this.editor = true
        this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation())
      },

      quitarImagen () {
        this.archivo = null
        this.form.imagen = null
        this.form.quitar_imagen = 1
      },

      guardar () {
        if (!this.$refs.form.validate()) return

        const id = this.form.idplantillas
        const payload = {
          titulo: this.form.titulo,
          body: this.form.body,
          recurrencia: this.form.recurrencia,
          activo: this.form.activo,
          quitar_imagen: this.form.quitar_imagen,
          escuela: this.escuela,
          botones: this.form.botones || [],
        }

        // Sin archivo: JSON (FormData a veces no entrega el campo botones en el PUT).
        // Con imagen: multipart y botones como string JSON.
        let datos = payload
        if (this.archivo) {
          datos = new FormData()
          Object.keys(payload).forEach((k) => {
            datos.append(k, k === 'botones' ? JSON.stringify(payload.botones) : payload[k])
          })
          datos.append('imagen', this.archivo)
        }

        const peticion = id
          ? this.$http.put('twilio.plantillas.catalogo/' + id, datos)
          : this.$http.post('twilio.plantillas.catalogo', datos)

        this.guardando = true
        return peticion.then((response) => {
          this.reemplazar(response.data)
          this.editor = false
          this.avisar(id ? 'Plantilla actualizada' : 'Plantilla creada')
        }).catch((error) => {
          this.validarError(error.response ? error.response.data.message : error.message)
        }).finally(() => { this.guardando = false })
      },

      cambiarActivo (item) {
        const activo = item.activo ? 0 : 1
        return this.$http.put('twilio.plantillas.catalogo/' + item.idplantillas, { activo }).then((response) => {
          this.reemplazar(response.data)
          this.avisar(activo ? 'Plantilla activada' : 'Plantilla desactivada')
        }).catch((error) => {
          this.validarError(error.response ? error.response.data.message : error.message)
        })
      },

      eliminar () {
        const item = this.porEliminar
        this.guardando = true
        return this.$http.delete('twilio.plantillas.catalogo/' + item.idplantillas).then(() => {
          this.plantillas = this.plantillas.filter((p) => p.idplantillas !== item.idplantillas)
          this.porEliminar = null
          this.avisar('Plantilla eliminada')
        }).catch((error) => {
          this.validarError(error.response ? error.response.data.message : error.message)
        }).finally(() => { this.guardando = false })
      },

      reemplazar (plantilla) {
        const i = this.plantillas.findIndex((p) => p.idplantillas === plantilla.idplantillas)
        if (i >= 0) this.plantillas.splice(i, 1, plantilla)
        else this.plantillas.unshift(plantilla)
      },

      textoConBotones (item, bodyOverride) {
        const body = bodyOverride != null ? bodyOverride : item.body
        const botones = listaBotones(item)
        if (!botones.length) return body
        const lineas = botones.map((b) => {
          if (b.tipo === 'url') return `[${b.texto}] ${b.url || ''}`
          if (b.tipo === 'telefono') return `[${b.texto}] ${b.telefono || ''}`
          return `[${b.texto}]`
        })
        return body + '\n\nBotones:\n' + lineas.join('\n')
      },

      cancelarFecha () {
        this.dialogFecha = false
        this.porCopiar = null
        this.cicloError = ''
      },

      cargarCicloParaCopia () {
        this.cicloFecha = null
        this.cicloNombre = ''
        this.cicloError = ''
        return this.$http.get('twilio.plantillas.catalogo.ciclo', { params: { escuela: this.escuela } }).then((response) => {
          this.cicloFecha = response.data.fecha_inicio_ciclo
          this.cicloNombre = response.data.ciclo || ''
          this.fechaModo = 'ciclo'
        }).catch((error) => {
          this.cicloError = (error.response && error.response.data && error.response.data.message)
            ? error.response.data.message
            : 'No se pudo obtener el ciclo'
          this.fechaModo = 'manual'
        })
      },

      async confirmarCopiaConFecha () {
        if (!this.porCopiar || !this.fechaElegida) return
        const body = aplicarFecha(this.porCopiar.body, this.fechaElegida)
        this.copiando = true
        try {
          await this.copiarAlPortapapeles(this.porCopiar, body)
          this.dialogFecha = false
          this.porCopiar = null
        } finally {
          this.copiando = false
        }
      },

      // Copia el texto y, si trae, la imagen. Si el navegador no deja copiar la imagen, copia al menos el texto.
      async copiar (item) {
        if (!navigator.clipboard) return this.validarError('Tu navegador no permite copiar automáticamente')
        if (tieneFecha(item.body)) {
          this.porCopiar = item
          this.fechaManual = hoyYmd()
          this.dialogFecha = true
          this.cargarCicloParaCopia()
          return
        }
        return this.copiarAlPortapapeles(item, item.body)
      },

      async copiarAlPortapapeles (item, body) {
        const texto = this.textoConBotones(item, body)
        try {
          if (!item.imagen) {
            await navigator.clipboard.writeText(texto)
            return this.avisar(listaBotones(item).length
              ? 'Texto y botones copiados, ya lo puedes pegar en el chat'
              : 'Texto copiado, ya lo puedes pegar en el chat')
          }
          const png = await this.imagenComoPng(item.imagen)
          await navigator.clipboard.write([new ClipboardItem({
            'text/plain': new Blob([texto], { type: 'text/plain' }),
            'image/png': png,
          })])
          this.avisar('Texto, botones e imagen copiados, ya los puedes pegar en el chat')
        } catch (e) {
          try {
            await navigator.clipboard.writeText(texto)
            this.avisar('Se copió el texto. La imagen no se pudo copiar: guárdala con clic derecho sobre ella')
          } catch (_) {
            this.validarError('No se pudo copiar')
          }
        }
      },

      // El portapapeles solo acepta PNG: si la imagen es JPG o WEBP se convierte.
      async imagenComoPng (nombre) {
        const archivo = await fetch(this.urlImagen(nombre)).then((res) => res.blob())
        if (archivo.type === 'image/png') return archivo

        const bitmap = await createImageBitmap(archivo)
        const lienzo = document.createElement('canvas')
        lienzo.width = bitmap.width
        lienzo.height = bitmap.height
        lienzo.getContext('2d').drawImage(bitmap, 0, 0)
        return new Promise((resolve, reject) => lienzo.toBlob((b) => (b ? resolve(b) : reject(new Error('png'))), 'image/png'))
      },

      avisar (texto) {
        this.aviso = { ver: true, texto }
      },
    },
  }
</script>

<style scoped>
  .plantillas-host--vista {
    width: 100%;
  }

  .plantillas {
    --chat-fondo: #efeae2;
    --chat-burbuja: #d9fdd3;
    --chat-texto: #111b21;
    --chat-tenue: #667781;
    --linea: #e3e6e8;
  }

  .plantillas--vista {
    min-height: calc(100vh - 120px);
  }

  .plantillas--vista .plantillas__cuerpo {
    min-height: 420px;
  }

  .plantillas__filtros {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 16px;
    padding: 12px 16px;
    border-bottom: 1px solid var(--linea);
  }

  .plantillas__buscar {
    flex: 1 1 260px;
    max-width: 380px;
  }

  .plantillas__cuerpo {
    padding: 16px !important;
    background: #f6f7f8;
    min-height: 320px;
  }

  .plantillas__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 16px;
  }

  .plantillas__vacio {
    max-width: 380px;
    margin: 48px auto;
    text-align: center;
    color: var(--chat-tenue);
  }

  .plantillas__vacio .text-subtitle-1 {
    color: var(--chat-texto);
  }

  .plantilla {
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(17, 27, 33, 0.08);
    overflow: hidden;
    transition: box-shadow 0.18s ease-out;
  }

  .plantilla:hover {
    box-shadow: 0 6px 18px rgba(17, 27, 33, 0.12);
  }

  .plantilla--inactiva .plantilla__chat {
    filter: grayscale(1);
    opacity: 0.6;
  }

  .plantilla__chat {
    background: var(--chat-fondo);
    padding: 14px 12px;
    display: flex;
    justify-content: flex-end;
    flex: 1 1 auto;
  }

  .plantilla__chat--grande {
    border-radius: 12px;
    min-height: 240px;
    align-items: flex-start;
  }

  .burbuja {
    max-width: 92%;
    background: var(--chat-burbuja);
    color: var(--chat-texto);
    border-radius: 8px 0 8px 8px;
    padding: 4px;
    box-shadow: 0 1px 1px rgba(11, 20, 26, 0.13);
  }

  .burbuja__img {
    display: block;
    width: 100%;
    max-height: 220px;
    object-fit: cover;
    border-radius: 6px;
  }

  .plantilla__chat--grande .burbuja__img {
    max-height: 320px;
    object-fit: contain;
    background: rgba(0, 0, 0, 0.04);
  }

  .burbuja__texto {
    padding: 6px 8px 4px;
    font-size: 14px;
    line-height: 1.45;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  .burbuja__texto--corto {
    display: -webkit-box;
    -webkit-line-clamp: 6;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .burbuja__texto--vacio {
    color: var(--chat-tenue);
    font-style: italic;
  }

  .burbuja__botones {
    display: flex;
    flex-direction: column;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    margin-top: 2px;
  }

  .burbuja__boton {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px 10px;
    font-size: 13px;
    font-weight: 500;
    color: #027eb5;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    text-align: center;
  }

  .burbuja__boton:first-child {
    border-top: none;
  }

  .burbuja >>> .burbuja__variable {
    background: rgba(0, 165, 72, 0.16);
    border-radius: 4px;
    padding: 0 3px;
    font-weight: 500;
  }

  .editor__opcional {
    font-weight: 400;
    color: #667781;
  }

  .editor__botones {
    margin-bottom: 16px;
  }

  .editor__boton {
    display: grid;
    grid-template-columns: 150px minmax(0, 1fr) auto;
    gap: 8px;
    align-items: flex-start;
    margin-bottom: 10px;
    padding: 10px;
    background: #f6f7f8;
    border-radius: 8px;
  }

  .editor__boton-extra {
    grid-column: 1 / -2;
  }

  .editor__boton-quitar {
    margin-top: 4px;
  }

  .plantilla__info {
    padding: 10px 14px 4px;
  }

  .plantilla__nombre {
    font-weight: 600;
    font-size: 14px;
    color: var(--chat-texto);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .plantilla__etiquetas {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 6px;
  }

  .etiqueta {
    display: inline-flex;
    align-items: center;
    font-size: 11px;
    line-height: 18px;
    padding: 0 8px;
    border-radius: 9px;
    background: #eef0f1;
    color: #3b4a54;
  }

  .etiqueta--inactiva {
    background: #fbe7e7;
    color: #a12a2a;
  }

  .plantilla__acciones {
    display: flex;
    align-items: center;
    padding: 2px 6px 6px 4px;
  }

  .editor__barra {
    border-bottom: 1px solid var(--linea, #e3e6e8);
  }

  .editor {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 340px);
    gap: 24px;
    padding: 20px !important;
  }

  .editor__label {
    font-size: 12px;
    font-weight: 600;
    color: #3b4a54;
    margin-bottom: 6px;
  }

  .editor__ayuda {
    font-size: 12px;
    color: #667781;
    margin-bottom: 16px;
  }

  .editor__imagen {
    margin-bottom: 12px;
  }

  .editor__fila {
    display: flex;
    gap: 16px;
    align-items: flex-start;
  }

  .editor__fila .v-select {
    flex: 1 1 auto;
  }

  .editor__preview {
    position: sticky;
    top: 0;
    align-self: start;
  }

  .editor__preview .plantilla__chat {
    --chat-fondo: #efeae2;
    --chat-burbuja: #d9fdd3;
    --chat-texto: #111b21;
    --chat-tenue: #667781;
  }

  @media (max-width: 760px) {
    .editor {
      grid-template-columns: minmax(0, 1fr);
    }

    .editor__preview {
      position: static;
      order: -1;
    }

    .plantilla__chat--grande {
      min-height: 0;
    }

    .editor__boton {
      grid-template-columns: minmax(0, 1fr) auto;
    }

    .editor__boton-tipo,
    .editor__boton-texto,
    .editor__boton-extra {
      grid-column: 1 / 2;
    }
  }
</style>
